import { useCurrentModal, useLottiePlayer } from '@starsoft/common/hooks';
import styles from './styles.module.scss';
import TransactionResultDetails from './Details';
import { Image, StaggerAnimation } from '@starsoft/common/components';
import PaymentExperience from './PaymentExperience';
import TransactionResultActions from './Actions';
import { Collapse } from '@mui/material';
import { TransactionResultProps } from './props';
import ExternalRedirectTrail from './Trail';
import { WalletService } from '@starsoft/common/services';
import { SettingsService } from '@starsoft/common/services/SettingsService';
import { DepositStatusId } from '@starsoft/common/models/deposit/status/id';
import { getMoneyOrAmount } from '@starsoft/common/utils';
import TransactionResultExpiresAt from './ExpiresAt';
import { CommonModalsKey } from '@starsoft/common/enums';

/**
 * TransactionResult Component
 * Displays the result of a transaction with animated visuals and detailed information.
 * Shows different states based on transaction status including pending, paid, expired etc.
 *
 * @component
 * @param {Object} props - Component props
 * @param {TransactionType} props.transaction - The transaction object containing status, amount, coin etc.
 * @returns {JSX.Element} A staggered animation container with transaction details
 *
 * @example
 * const transaction = {
 *   status: { id: "1" }, // DepositStatusId.Paid
 *   amount: 100,
 *   coin: {
 *     image: "btc.svg",
 *     name: "Bitcoin"
 *   }
 * };
 *
 * <TransactionResult transaction={transaction} />
 */
export default function TransactionResult({
  transaction,
}: TransactionResultProps) {
  const modal = useCurrentModal(CommonModalsKey);

  /**
   * Determines which Lottie animation file to use based on transaction status
   * @returns {string | undefined} The name of the Lottie animation file to use
   */
  function getLottie(): string | undefined {
    switch (transaction?.status?.id?.toString()) {
      case DepositStatusId.Paid?.toString():
        return 'check.json';
      case DepositStatusId.Expired?.toString():
      case DepositStatusId.Refunded?.toString():
        return 'error.json';
      case DepositStatusId.Pending?.toString():
      case DepositStatusId.Created?.toString():
      default:
        return 'processing.json';
    }
  }

  const { lottieAnimationRef } = useLottiePlayer({
    path: `/${getLottie()}`,
    dependecyArray: [transaction],
  });

  return (
    <StaggerAnimation
      direction="column"
      stagger={0.15}
      staggerDirection="down"
      className={styles.container}
    >
      <div className={styles.container__column}>
        <div className={styles['container__animated-coin']}>
          {transaction?.status?.id?.toString() ===
            DepositStatusId.Pending?.toString() && (
            <div
              className={styles['container__animated-coin__particle-container']}
            >
              <div
                className={
                  styles[
                    'container__animated-coin__particle-container__particle'
                  ]
                }
              />
            </div>
          )}
          <Image
            className={styles['container__animated-coin__coin']}
            format="square"
            width={64}
            height={64}
            src={`${SettingsService.env.S3_BUCKET_BASE_URL}/coins/rounded/${
              transaction?.coin?.image ?? 'brl.svg'
            }`}
            alt={transaction?.coin?.name}
          />
        </div>
        {transaction?.status?.id?.toString() !==
          DepositStatusId.Pending?.toString() && (
          <div ref={lottieAnimationRef} className={styles.container__lottie} />
        )}

        <div className={styles.container__row}>
          <div className={styles.container__value}>
            {WalletService.maskCurrency({
              amount: getMoneyOrAmount(transaction?.amount),
              coin: transaction?.coin,
            })}
          </div>
        </div>
        {transaction?.status?.id?.toString() ===
          DepositStatusId.Created?.toString() &&
          modal === CommonModalsKey.Deposit && (
            <TransactionResultExpiresAt transaction={transaction} />
          )}
        {transaction?.status?.id?.toString() !==
          DepositStatusId.Pending?.toString() && (
          <div ref={lottieAnimationRef} className={styles.container__lottie} />
        )}

        <TransactionResultDetails transaction={transaction} />
        <ExternalRedirectTrail transaction={transaction} />
      </div>

      <Collapse
        in={
          transaction?.status?.id?.toString() !==
          DepositStatusId.Pending?.toString()
        }
      >
        <div className={styles.container__column}>
          <TransactionResultActions />
          <PaymentExperience />
        </div>
      </Collapse>
    </StaggerAnimation>
  );
}
